// ISMÉTLŐDŐ MEGBESZÉLÉS — a szabály kibontása alkalmakra. EGY helyen él, mert két hívója van: a BFF (a hiteles
// kibontás: ezek a sorok íródnak, ezekre fut az ütközés-ellenőrzés) és a web (az űrlap előnézete: „12 alkalom:
// … – …"). Ha a kettő eltérne, a felület mást ígérne, mint ami létrejön.
//
// FALIÓRA-SZABÁLY: a „minden kedden 10:00" a HELYI (alapból Europe/Budapest) falióra szerint értendő — a
// téli/nyári időszámítás váltásán át is 10:00 marad (az UTC-instant eltolódik egy órával). Az időtartam
// (vég − kezdet) alkalmanként változatlan.
//
// Függőségmentes (a csomag csak zod-ot használ): a zóna-átváltás `Intl`-lel megy.
/** Egy sorozat legfeljebb ennyi alkalomból állhat (a résztvevőkkel együtt is egy kezelhető méretű köteg). */
export const MAX_SERIES_OCCURRENCES = 52;
/** Egy sorozatnak legalább ennyi alkalma van — egy alkalom nem sorozat. */
export const MIN_SERIES_OCCURRENCES = 2;
const DAY_MS = 86_400_000;
/** Biztonsági korlát a nap-iterációra (két év) — egy hibás szabály se pörögjön végtelenül. */
const MAX_SCAN_DAYS = 731;
/** Egy instant helyi falióra-komponensei a megadott zónában. */
function wallParts(ms, timeZone) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    }).formatToParts(new Date(ms));
    const get = (type) => Number(parts.find((p) => p.type === type)?.value ?? '0');
    return {
        y: get('year'),
        m: get('month'),
        d: get('day'),
        h: get('hour') === 24 ? 0 : get('hour'),
        min: get('minute'),
        s: get('second'),
    };
}
/** A zóna UTC-eltolása (ms) egy adott instantban. */
function offsetMs(ms, timeZone) {
    const p = wallParts(ms, timeZone);
    return Date.UTC(p.y, p.m - 1, p.d, p.h, p.min, p.s) - Math.floor(ms / 1000) * 1000;
}
/** Helyi falióra (nap + óra:perc:mp) → UTC instant (ms), DST-helyesen (két lépéses eltolás-korrekció). */
function wallToUtc(dayMs, h, min, s, timeZone) {
    const naive = dayMs + ((h * 60 + min) * 60 + s) * 1000;
    let guess = naive - offsetMs(naive, timeZone);
    guess = naive - offsetMs(guess, timeZone);
    return guess;
}
/** `YYYY-MM-DD` → az adott nap 00:00 UTC-je (naptári nap-aritmetikához, zóna nélkül). */
function parseDay(day) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
    if (!m)
        return null;
    return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}
/** ISO hét-nap (1 = hétfő … 7 = vasárnap) egy naptári napra. */
function isoWeekday(dayMs) {
    return ((new Date(dayMs).getUTCDay() + 6) % 7) + 1;
}
/**
 * A szabály kibontása. Az első alkalom MAGA a megadott kezdés (ezért a heti/kétheti szabálynál a kezdő napnak a
 * kiválasztott napok egyikének kell lennie). A havi szabály a hónap ugyanazon napján ismétel; ha egy hónapban
 * nincs ilyen nap (pl. 31.), az a hónap kimarad.
 */
export function expandRecurrence(startsAt, endsAt, rule, timeZone = 'Europe/Budapest') {
    const startMs = Date.parse(startsAt);
    const endMs = Date.parse(endsAt);
    if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) {
        return { ok: false, error: 'invalidStart' };
    }
    const hasUntil = typeof rule.untilDate === 'string' && rule.untilDate !== '';
    const hasCount = typeof rule.occurrenceCount === 'number';
    if (!hasUntil && !hasCount)
        return { ok: false, error: 'endRequired' };
    if (hasUntil && hasCount)
        return { ok: false, error: 'endAmbiguous' };
    if (hasCount) {
        if (rule.occurrenceCount < MIN_SERIES_OCCURRENCES)
            return { ok: false, error: 'tooFew' };
        if (rule.occurrenceCount > MAX_SERIES_OCCURRENCES)
            return { ok: false, error: 'tooMany' };
    }
    const wall = wallParts(startMs, timeZone);
    const startDay = Date.UTC(wall.y, wall.m - 1, wall.d);
    const untilDay = hasUntil ? parseDay(rule.untilDate) : null;
    if (hasUntil && untilDay === null)
        return { ok: false, error: 'endRequired' };
    if (untilDay !== null && untilDay < startDay)
        return { ok: false, error: 'untilBeforeStart' };
    const weekly = rule.frequency === 'weekly' || rule.frequency === 'biweekly';
    const weekdays = new Set(rule.weekdays);
    if (weekly) {
        if (weekdays.size === 0)
            return { ok: false, error: 'weekdaysRequired' };
        if (!weekdays.has(isoWeekday(startDay)))
            return { ok: false, error: 'startNotInWeekdays' };
    }
    // a munkanapos sorozat első alkalma (maga a kezdés) se essen hétvégére
    if (rule.frequency === 'workdays' && isoWeekday(startDay) > 5) {
        return { ok: false, error: 'startNotInWeekdays' };
    }
    // a kezdő hét hétfője — a kétheti szabály hét-párosságának alapja
    const startMonday = startDay - (isoWeekday(startDay) - 1) * DAY_MS;
    const matches = (dayMs) => {
        const wd = isoWeekday(dayMs);
        switch (rule.frequency) {
            case 'weekly':
                return weekdays.has(wd);
            case 'biweekly':
                return weekdays.has(wd) && Math.floor((dayMs - startMonday) / DAY_MS / 7) % 2 === 0;
            case 'workdays':
                return wd <= 5;
            case 'monthly':
                return new Date(dayMs).getUTCDate() === wall.d;
        }
    };
    const duration = endMs - startMs;
    const occurrences = [];
    const target = hasCount ? rule.occurrenceCount : Number.POSITIVE_INFINITY;
    // a havi szabály egy-két éven át is ritka — a nap-iteráció korlátja ezért a havira hosszabb
    const scanLimit = rule.frequency === 'monthly' ? MAX_SCAN_DAYS * 3 : MAX_SCAN_DAYS;
    for (let i = 0; i <= scanLimit && occurrences.length < target; i += 1) {
        const day = startDay + i * DAY_MS;
        if (untilDay !== null && day > untilDay)
            break;
        if (i > 0 && !matches(day))
            continue;
        if (occurrences.length >= MAX_SERIES_OCCURRENCES)
            return { ok: false, error: 'tooMany' };
        const s = i === 0 ? startMs : wallToUtc(day, wall.h, wall.min, wall.s, timeZone);
        occurrences.push({
            startsAt: new Date(s).toISOString(),
            endsAt: new Date(s + duration).toISOString(),
        });
    }
    if (occurrences.length < MIN_SERIES_OCCURRENCES)
        return { ok: false, error: 'tooFew' };
    return { ok: true, occurrences };
}
/**
 * Egy SOROZAT-ALKALOM áthelyezése egy minta-időpont falióra-idejére: a cél-alkalom HELYI napja marad, a kezdés
 * a minta helyi óra:perce lesz, az időtartam a mintáé. Erre épül a „sorozat szerkesztése" (ez és a következők /
 * mind): a felhasználó egy alkalmon állítja át 10:00-ról 11:00-ra, és minden érintett alkalom a SAJÁT napján
 * lesz 11:00 — a DST-váltáson át is.
 */
export function applyWallTime(occurrenceStartsAt, templateStartsAt, templateEndsAt, timeZone = 'Europe/Budapest') {
    const occMs = Date.parse(occurrenceStartsAt);
    const tStart = Date.parse(templateStartsAt);
    const tEnd = Date.parse(templateEndsAt);
    if (Number.isNaN(occMs) || Number.isNaN(tStart) || Number.isNaN(tEnd) || tEnd <= tStart)
        return null;
    const day = wallParts(occMs, timeZone);
    const time = wallParts(tStart, timeZone);
    const start = wallToUtc(Date.UTC(day.y, day.m - 1, day.d), time.h, time.min, time.s, timeZone);
    return {
        startsAt: new Date(start).toISOString(),
        endsAt: new Date(start + (tEnd - tStart)).toISOString(),
    };
}
/** Egy instant HELYI naptári napja (`YYYY-MM-DD`) a zónában. */
export function localDay(iso, timeZone = 'Europe/Budapest') {
    const ms = Date.parse(iso);
    if (Number.isNaN(ms))
        return null;
    const p = wallParts(ms, timeZone);
    return `${String(p.y).padStart(4, '0')}-${String(p.m).padStart(2, '0')}-${String(p.d).padStart(2, '0')}`;
}
//# sourceMappingURL=meeting-recurrence.js.map